<template>
  <validation-observer tag="div" ref="valiObjserver">
    <div class="mb-8">
      <h1 class="fs-20 font-medium mb-4">Enquiry summary</h1>
      <div class="flex mb-2">
        <svg width="24" height="24" class="btn-icon mr-4">
          <use xlink:href="/img/sprite.svg#ic_calendar"></use>
        </svg>
        {{ dateDisplay }}
      </div>
      <div class="flex">
        <svg width="24" height="24" class="btn-icon mr-4">
          <use xlink:href="/img/sprite.svg#ic_people"></use>
        </svg>
        {{ guestsDisplay }}
      </div>
    </div>

    <div class="mb-8">
      <h1 class="fs-20 font-medium mb-4">Projects</h1>
      <div v-for="item in projects" :key="item.id" class="flex">
        <span>{{ item.qty }} x</span>
        <span class="mx-4">{{ item.name }}</span>
        <span class="flex-1 text-right">
          <currency-context>
            <template v-slot="{ convert }">
              <span
                v-text="convert(item.total_price)"
              ></span>
            </template>
          </currency-context>
           /{{ item.price_type.toLowerCase() }}
        </span>
      </div>
    </div>

    <form @submit.prevent="onBeforeSubmit">
      <input type="hidden" id="zc_gad" name="zc_gad" value="" />

      <label class="block mb-8">
        <p class="mb-2 fs-14">First name</p>
        <validation-provider
          ref="First Name"
          name="First Name"
          rules="required"
          v-slot="{ errors }"
        >
          <input
            class="input input-tall w-full"
            :class="{ 'input--error': errors[0] }"
            v-model="firstName"
          />
          <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
        </validation-provider>
      </label>

      <label class="block mb-8">
        <p class="mb-2 fs-14">Last name</p>
        <validation-provider
          ref="Last Name"
          name="Last Name"
          rules="required"
          v-slot="{ errors }"
        >
          <input
            class="input input-tall w-full"
            :class="{ 'input--error': errors[0] }"
            v-model="lastName"
          />
          <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
        </validation-provider>
      </label>

      <label class="block mb-8">
        <p class="mb-2 fs-14">Email</p>
        <validation-provider
          ref="Email"
          name="Email"
          rules="required|email"
          v-slot="{ errors }"
        >
          <input
            class="input input-tall w-full"
            :class="{ 'input--error': errors[0] }"
            v-model.lazy="email"
          />
          <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
        </validation-provider>
      </label>

      <label class="relative block mb-8 w-4/5 sm:w-full">
        <p class="mb-2 fs-14">Country</p>
        <validation-provider
          ref="Country"
          name="Country"
          rules="required"
          v-slot="{ errors }"
        >
          <div class="flex items-center">
            <select
              class="cursor-pointer input-select input input-tall w-full"
              :class="{ 'input--error': errors[0] }"
              v-model="country"
            >
              <option value="" disabled>Select country</option>
              <option
                v-for="item in COUNTRY_CODES"
                :key="item.code"
                :value="item.code"
              >
                {{ item.name }}
              </option>
            </select>
            <svg
              width="24"
              height="24"
              class="flex-none booking-widget-input-icon--right pointer-events-none"
            >
              <use xlink:href="/img/sprite.svg#ic_chevron-down"></use>
            </svg>
          </div>
          <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
        </validation-provider>
      </label>

      <label class="block mb-8 w-4/5 sm:w-full">
        <p class="mb-2 fs-14">Phone</p>
        <validation-provider
          ref="Phone"
          name="Phone"
          rules="required"
          v-slot="{ errors }"
        >
          <phone-input
            class="input input-tall w-full flex-1"
            :class="{ 'input--error': errors[0] }"
            v-model="phone"
          />

          <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
        </validation-provider>
      </label>

      <label class="relative block mb-8 w-4/5 sm:w-full">
        <p class="mb-2 fs-14">Experience level</p>
        <validation-provider
          ref="Experience level"
          name="Experience level"
          rules="required"
          v-slot="{ errors }"
        >
          <div class="flex items-center">
            <select
              class="cursor-pointer input-select input input-tall w-full"
              :class="{ 'input--error': errors[0] }"
              v-model="experienceLevel"
            >
              <option value="" disabled>Select experience level</option>
              <option
                v-for="item in EXPERIENCE_LEVEL"
                :key="item.value"
                :value="item.text"
              >
                {{ item.text }}
              </option>
            </select>
            <svg
              width="24"
              height="24"
              class="flex-none booking-widget-input-icon--right pointer-events-none"
            >
              <use xlink:href="/img/sprite.svg#ic_chevron-down"></use>
            </svg>
          </div>
          <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
        </validation-provider>
      </label>

      <label class="relative block mb-8 sm:w-full">
        <p class="mb-2 fs-14">Date of birth</p>
        <div class="flex flex-nowrap">
          <validation-provider
            name="Year"
            vid="year"
            rules="required"
            tag="div"
            class="mr-4 relative flex-1"
            v-slot="{ errors }"
          >
            <div class="flex items-center">
              <select
                class="cursor-pointer input-select input input-tall w-full"
                :class="{ 'input--error': errors[0] }"
                v-model="dob.year"
              >
                <option value="" disabled>Year</option>
                <option
                  v-for="year in selectableYears"
                  :key="year"
                  :value="year"
                >
                  {{ year }}
                </option>
              </select>
              <svg
                width="24"
                height="24"
                class="flex-none booking-widget-input-icon--right pointer-events-none"
              >
                <use xlink:href="/img/sprite.svg#ic_chevron-down"></use>
              </svg>
            </div>
          </validation-provider>

          <validation-provider
            class="mr-4 relative flex-1"
            name="Month"
            vid="month"
            rules="required"
            tag="div"
            v-slot="{ errors }"
          >
            <div class="flex items-center">
              <select
                class="cursor-pointer input-select input input-tall w-full"
                :class="{ 'input--error': errors[0] }"
                v-model="dob.month"
              >
                <option value="" disabled>Month</option>
                <option
                  v-for="item in selectableMonths"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.display }}
                </option>
              </select>
              <svg
                width="24"
                height="24"
                class="flex-none booking-widget-input-icon--right pointer-events-none"
              >
                <use xlink:href="/img/sprite.svg#ic_chevron-down"></use>
              </svg>
            </div>
          </validation-provider>

          <validation-provider
            tag="div"
            name="Day"
            class="relative flex-1"
            rules="required"
            v-slot="{ errors }"
          >
            <div class="flex items-center">
              <select
                class="cursor-pointer input-select input input-tall w-full"
                :class="{ 'input--error': errors[0] }"
                v-model="dob.day"
              >
                <option value="" disabled>Day</option>
                <option v-for="day in selectableDays" :key="day" :value="day">
                  {{ day }}
                </option>
              </select>
              <svg
                width="24"
                height="24"
                class="flex-none booking-widget-input-icon--right pointer-events-none"
              >
                <use xlink:href="/img/sprite.svg#ic_chevron-down"></use>
              </svg>
            </div>
          </validation-provider>
        </div>
        <span v-if="!isDobValid" class="fs-14 text-danger mt-1"
          >Please enter your Date of birth</span
        >
      </label>

      <label class="mb-8 block">
        <p class="mb-2 fs-14">Special requests (Optional)</p>
        <textarea
          class="input w-full enquiry-additional-request"
          v-model="additionalMessage"
          placeholder="Please let us know how many weeks you are keen to join as well as any other important questions or information (such as dietary requirements) ..."
        />
      </label>

      <div class="flex">
        <div class="flex-none">
          <checkbox v-model="newsletter" />
        </div>

        <p class="flex-1">
          Tick the box to opt into ZuBlu’s news from the blue. Exciting updates
          about the ocean world you won’t want to miss. And don’t worry, we only
          send you the really good stuff and never share your details.
        </p>
      </div>

      <button
        class="mt-8 btn btn-primary input-tall font-medium sm:w-full"
        :disabled="isWorking"
      >
        <div class="flex justify-center">
          <loading-indicator width="20" class="mr-2" v-show="isWorking" />
          <span>Request a quote</span>
        </div>
      </button>
    </form>
  </validation-observer>
</template>

<script>
import {
  ValidationProvider,
  ValidationObserver,
  extend,
  setInteractionMode,
} from "vee-validate";

import Tooltip from "../../components/core/Tooltip";
import Checkbox from "../../components/core/Checkbox";
import CurrencyContext from "../../components/core/CurrencyContext";
import { required, email, min_value } from "vee-validate/dist/rules";
import range from "lodash/range";
import LoadingIndicator from "../../components/core/LoadingIndicator";
import PhoneInput from "../../components/core/PhoneInput";
import DateOfBirthPicker from "./DateOfBirthPicker";
import {
  getBookingDateDisplay,
  getBookingTravelersDisplay,
} from "../../helpers";

import { EXPERIENCE_LEVEL, COUNTRY_CODES } from "../../helpers/constants";

setInteractionMode("eager");
extend("email", email);
extend("min_value", min_value);
extend("required", {
  ...required,
  message: (field) => `Please enter your ${field}`,
});

let sortDesc = (a, b) => {
  if (a < b) {
    return 1;
  }
  if (a > b) {
    return -1;
  }
  return 0;
};

const fieldsOrder = [
  "First Name",
  "Last Name",
  "Email",
  "Phone",
  "Experience level",
  "Date of birth",
];

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    PhoneInput,
    DateOfBirthPicker,
    LoadingIndicator,
    Checkbox,
    Tooltip,
    CurrencyContext
  },
  name: "Enquiry",
  props: ["resortId", "destinationSku"],
  data() {
    return {
      isDobValid: true,
      EXPERIENCE_LEVEL,
      COUNTRY_CODES,
      isWorking: false,
      firstName: "",
      lastName: "",
      email: "",
      country: "",
      phone: "",
      experienceLevel: "",
      dob: {
        year: "",
        month: "",
        day: "",
      },
      additionalMessage: "",
      newsletter: true,
      bookingInfo: {
        date: [null, null],
        items: [],
        guestNum: 0,
      },
    };
  },
  created() {
    window.addEventListener("beforeunload", this.sendGoogleAnalytics);
  },
  computed: {
    isMobile() {
      return window.matchMedia("only screen and (max-width: 1200px)").matches;
    },
    dateDisplay() {
      const [from, to] = this.bookingInfo.date;
      return getBookingDateDisplay(from, to).join(" — ");
    },
    selectableYears() {
      const RANGE_LENGTH = 100;
      const currentYear = moment().year();
      let startYear = currentYear - RANGE_LENGTH;
      if (startYear < 0) {
        startYear = 0;
      }
      const endYear = currentYear - 10;
      return range(startYear, endYear).sort(sortDesc);
    },
    selectableMonths() {
      const months = moment.monthsShort();
      return months.map((month, index) => ({
        value: index + 1,
        display: month,
      }));
    },
    selectableDays() {
      if (this.dob.year && this.dob.month) {
        const days = moment(
          `${this.dob.year}-${this.dob.month}`,
          "YYYY-M"
        ).daysInMonth();
        return range(1, days + 1);
      }
      return [];
    },
    guestsDisplay() {
      return getBookingTravelersDisplay(String(this.bookingInfo.guestNum));
    },
    projects() {
      return this.bookingInfo.items;
    },
  },
  mounted() {
    // https://zubludiving.atlassian.net/wiki/spaces/EN/pages/371654686/Widget+Integration
    const queries = new URLSearchParams(location.search);
    const fromDate = moment(queries.get("from_date") || "");
    const toDate = moment(queries.get("to_date") || "");
    const guestNum = Number(queries.get("guest_num") || 0);

    let currDate = [].concat(this.bookingInfo.date);
    currDate[0] = fromDate;
    if (toDate.isValid() && fromDate.isBefore(toDate)) {
      currDate[1] = toDate;
    }

    this.bookingInfo.date = currDate;
    this.bookingInfo.guestNum = guestNum;

    const extractedItems = location.search
      .substring(location.search.indexOf("?") ? 1 : 0)
      .split("&")
      .map((s) => ({
        name: s.substring(0, s.indexOf("=")),
        value: s.substring(s.indexOf("=") + 1),
      }))
      .filter((s) => !isNaN(s.name))
      .map((s) => ({ id: s.name, qty: parseInt(s.value) }));

    const payload = {
      method: "preview",
      activities: extractedItems,
      guest_num: guestNum,
      supplier_id: this.resortId,
      trip: {
        from_date: fromDate.isValid() ? fromDate.format("YYYY-MM-DD") : "",
        to_date: toDate.isValid() ? toDate.format("YYYY-MM-DD") : "",
      },
    };

    $.ajax({
      method: "POST",
      url: window.ZUBLU_VARIABLES.ODYSSEY_API + "/api/v1/ecoventure/booking",
      data: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
    })
      .done((res) => {
        const { activities } = res;
        this.bookingInfo.items = activities;
      })
      .fail(console.error.bind(console));
  },
  methods: {
    toggleNewsLetter() {
      this.newsletter = !this.newsletter;
    },
    sendGoogleAnalytics() {
      if (!this.isWorking) {
        window.pushDataLayer("zublu_cta", {
          cta_type: "enquiry",
          cta_value: "enquiry_leave",
          cta_screen: window.location.pathname,
        });
      }
    },
    checkBeforeSubmit() {
      const [from, _] = this.bookingInfo.date;
      const guestNum = this.bookingInfo.guestNum;

      return Boolean(from && guestNum);
    },
    onBeforeSubmit() {
      const { Day, year, month } = this.$refs.valiObjserver.errors;
      const dobErrors = [...year, ...month, ...Day];
      this.isDobValid = dobErrors.length < 1;

      this.$refs.valiObjserver.validate().then((result) => {
        if (result === false) {
          setTimeout(() => {
            const errors = JSON.parse(
              JSON.stringify(this.$refs.valiObjserver.errors)
            );

            for (const field of fieldsOrder) {
              if (errors[field] && errors[field].length) {
                this.$refs[field].$el.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                break;
              }
            }
          }, 100); //https://github.com/logaretm/vee-validate/issues/2248
        } else {
          this.onSubmit();
        }
      });
    },
    onSubmit() {
      const canSubmit = this.checkBeforeSubmit();

      if (canSubmit) {
        this.isWorking = true;

        const [fromDate, toDate] = this.bookingInfo.date;
        const guestNum = this.bookingInfo.guestNum;
        const activities = this.bookingInfo.items;

        const payload = {
          method: "submit",
          activities,
          guest_num: guestNum,
          supplier_id: this.resortId,
          details: {
            first_name: this.firstName,
            last_name: this.lastName,
            email: this.email,
            country: this.country,
            phone: this.phone,
            experience_level: this.experienceLevel,
            dob: moment(
              `${this.dob.year}-${this.dob.month}-${this.dob.day}`,
              "YYYY-M-DD"
            ).format("YYYY-MM-DD"),
            remarks: this.additionalMessage,
          },
          trip: {
            from_date:
              fromDate && fromDate.isValid()
                ? fromDate.format("YYYY-MM-DD")
                : "",
            to_date:
              toDate && toDate.isValid() ? toDate.format("YYYY-MM-DD") : "",
          },
          newsletter: this.newsletter,
          session: window.Craft.session,
        };

        try {
          $.ajax({
            method: "POST",
            url: window.ZUBLU_VARIABLES.ODYSSEY_API + "/api/v1/okeanos_proxy",
            data: JSON.stringify({
              payload: payload,
            }),
            headers: { "Content-Type": "application/json" },
          });
        } catch (error) {}

        const url =
          window.ZUBLU_VARIABLES.ODYSSEY_API + "/api/v1/ecoventure/booking";
        $.ajax({
          method: "POST",
          url,
          data: JSON.stringify(payload),
          headers: { "Content-Type": "application/json" },
        })
          .done(() => {
            window.pushDataLayer("enquiry", {
              submit_location: "main_site",
              submit_type: "direct_ecoventure",
            });

            const fullName = `${this.firstName} ${this.lastName}`;
            window.location.href = `/enquiry-success?user=${fullName}&resortId=${this.resortId}`;
          })
          .fail((e) => {
            this.isWorking = false;

            window.pushDataLayer("zublu_cta", {
              cta_type: "enquiry",
              cta_value: "enquiry_error",
              cta_screen: window.location.pathname,
            });
          });
      }
    },
  },
};
</script>

<style scoped>
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.zb-spin {
  position: relative;
}

.zb-spin::before,
.zb-spin::after {
  content: "";
  position: absolute;
}

.zb-spin::before {
  border-radius: 50%;
  background: linear-gradient(
        0deg,
        hsla(0, 0%, 100%, 0) 50%,
        hsla(0, 0%, 100%, 0.3) 100%
      )
      0% 0%,
    linear-gradient(
        90deg,
        hsla(0, 0%, 100%, 0.3) 0%,
        hsla(0, 0%, 100%, 0.6) 100%
      )
      100% 0%,
    linear-gradient(
        180deg,
        hsla(0, 0%, 100%, 0.6) 0%,
        hsla(0, 0%, 100%, 0.9) 100%
      )
      100% 100%,
    linear-gradient(
        360deg,
        hsla(0, 0%, 100%, 0.9) 0%,
        hsla(0, 0%, 100%, 1) 100%
      )
      0% 100%;
  background-repeat: no-repeat;
  background-size: 50% 50%;
  top: -1px;
  bottom: -1px;
  left: -1px;
  right: -1px;
}

.zb-spin::after {
  background: red;
  border-radius: 50%;
  top: 3%;
  bottom: 3%;
  left: 3%;
  right: 3%;
}
</style>
